<template>
  <div class="text-sm">
    <!-- Просмотр: только человекочитаемая сводка, без ключей и id -->
    <div v-if="readOnly" class="text-gray-700">{{ summary }}</div>

    <div v-else class="space-y-2">
      <div class="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-2">
        <!-- Контур -->
        <div>
          <label class="block text-xs text-gray-500 mb-1">Контур</label>
          <div class="v-select-filter" style="width: 100%; flex: none;">
            <v-select
              v-model="scope"
              :options="scopeOptions"
              :reduce="o => o.value"
              :get-option-label="o => o.label"
              placeholder="Без привязки"
              @update:modelValue="onScopeChange"
            />
          </div>
        </div>

        <!-- Производственный контур: заказ → изделие → операция -->
        <template v-if="scope === 'production'">
          <div>
            <label class="block text-xs text-gray-500 mb-1">Производственный заказ</label>
            <div class="v-select-filter" style="width: 100%; flex: none;">
              <v-select
                v-model="selectedOrder"
                :options="orderOptions"
                :loading="ordersLoading"
                :get-option-label="o => o.label"
                placeholder="Выберите заказ"
                @open="ensureOrders"
                @update:modelValue="onOrderChange"
              >
                <template #option="o"><span>{{ o.label }}</span></template>
                <template #no-options>
                  <span v-if="ordersLoading">Загрузка...</span>
                  <span v-else-if="ordersError" class="text-red-600">Не удалось загрузить заказы</span>
                  <span v-else>Ничего не найдено</span>
                </template>
                <template #list-footer>
                  <li v-if="ordersHasMore" class="px-3 py-2 text-center border-t">
                    <button
                      type="button"
                      @click.prevent.stop="loadMoreOrders"
                      :disabled="ordersLoading"
                      class="text-blue-600 hover:text-blue-800 disabled:text-gray-400"
                    >
                      {{ ordersLoading ? 'Загрузка...' : 'Загрузить ещё...' }}
                    </button>
                  </li>
                </template>
              </v-select>
            </div>
          </div>

          <div>
            <label class="block text-xs text-gray-500 mb-1">Изделие (серийный номер)</label>
            <div class="v-select-filter" style="width: 100%; flex: none;">
              <v-select
                v-model="selectedItem"
                :options="itemOptions"
                :loading="orderDetailLoading"
                :get-option-label="o => o.label"
                :disabled="!selectedOrder"
                placeholder="Выберите изделие"
                @update:modelValue="onItemChange"
              >
                <template #option="o"><span>{{ o.label }}</span></template>
                <template #no-options>
                  <span v-if="orderDetailLoading">Загрузка...</span>
                  <span v-else-if="orderDetailError" class="text-red-600">Не удалось загрузить состав заказа</span>
                  <span v-else-if="!selectedOrder">Сначала выберите заказ</span>
                  <span v-else>В заказе нет изделий</span>
                </template>
              </v-select>
            </div>
          </div>

          <div>
            <label class="block text-xs text-gray-500 mb-1">Операция задания</label>
            <div class="v-select-filter" style="width: 100%; flex: none;">
              <v-select
                v-model="selectedOperation"
                :options="operationOptions"
                :get-option-label="o => o.label"
                :disabled="!selectedItem"
                placeholder="Не указана"
                @update:modelValue="onOperationChange"
              >
                <template #option="o"><span>{{ o.label }}</span></template>
                <template #no-options>
                  <span v-if="!selectedItem">Сначала выберите изделие</span>
                  <span v-else>У задания нет операций</span>
                </template>
              </v-select>
            </div>
          </div>
        </template>

        <!-- Приёмочный контур: накладная → позиция с партией -->
        <template v-else-if="scope === 'receipt'">
          <div>
            <label class="block text-xs text-gray-500 mb-1">Приходная накладная</label>
            <div class="v-select-filter" style="width: 100%; flex: none;">
              <v-select
                v-model="selectedInvoice"
                :options="invoiceOptions"
                :filterable="false"
                :loading="invoicesLoading"
                :get-option-label="o => o.label"
                placeholder="Поиск по номеру или поставщику"
                @search="onInvoiceSearch"
                @open="() => onInvoiceSearch('')"
                @update:modelValue="onInvoiceChange"
              >
                <template #option="o"><span>{{ o.label }}</span></template>
                <template #no-options>
                  <span v-if="invoicesLoading">Загрузка...</span>
                  <span v-else-if="invoicesError" class="text-red-600">Не удалось загрузить накладные</span>
                  <span v-else>Ничего не найдено</span>
                </template>
              </v-select>
            </div>
          </div>

          <div>
            <label class="block text-xs text-gray-500 mb-1">Позиция накладной (партия)</label>
            <div class="v-select-filter" style="width: 100%; flex: none;">
              <v-select
                v-model="selectedPosition"
                :options="positionOptions"
                :loading="invoiceDetailLoading"
                :get-option-label="o => o.label"
                :disabled="!selectedInvoice"
                placeholder="Не указана"
                @update:modelValue="onPositionChange"
              >
                <template #option="o"><span>{{ o.label }}</span></template>
                <template #no-options>
                  <span v-if="invoiceDetailLoading">Загрузка...</span>
                  <span v-else-if="invoiceDetailError" class="text-red-600">Не удалось загрузить позиции накладной</span>
                  <span v-else-if="!selectedInvoice">Сначала выберите накладную</span>
                  <span v-else>В накладной нет позиций</span>
                </template>
              </v-select>
            </div>
          </div>
        </template>
      </div>

      <!-- Сбой загрузки справочника: пустой список и отказ должны различаться -->
      <p v-if="errorMessage" class="text-xs text-red-600">
        {{ errorMessage }}
        <button
          v-if="retryAction"
          type="button"
          @click="retryLastLoad"
          class="ml-1 text-blue-600 hover:underline"
        >
          Повторить
        </button>
      </p>

      <p class="text-xs text-gray-500">{{ summary }}</p>
    </div>
  </div>
</template>

<script>
// Выбор конкретного экземпляра для строки документа качества (AND-211, ТЗ-7.3).
// Производственный контур: заказ, изделие с серийным номером, задание и операция.
// Приёмочный: приходная накладная и партия изготовления.
// Все поля необязательные — ручной выбор номенклатуры без изделия продолжает работать.
import {
  SCOPE_PRODUCTION,
  SCOPE_RECEIPT,
  emptyBinding,
  bindingScope,
  bindingSummary,
  productionOrderLabel,
  batchProductLabel,
  taskOperationLabel,
  receiptInvoiceLabel,
  batchManufactureLabel,
  loadedProductionOrders,
  productionOrdersHasMore,
  loadMoreProductionOrders,
  loadProductionOrder,
  searchReceiptInvoices,
  loadReceiptInvoice,
} from '@/components/smkDesk/productBinding.js';

const SCOPE_OPTIONS = [
  { value: SCOPE_PRODUCTION, label: 'Производство' },
  { value: SCOPE_RECEIPT, label: 'Приёмка' },
];

export default {
  name: 'ProductBindingSelector',
  props: {
    modelValue: { type: Object, default: null },
    readOnly: { type: Boolean, default: false },
  },
  emits: ['update:modelValue', 'product-picked'],
  data() {
    return {
      scope: null,
      scopeOptions: SCOPE_OPTIONS,
      selectedOrder: null,
      selectedItem: null,
      selectedOperation: null,
      selectedInvoice: null,
      selectedPosition: null,
      orderOptions: [],
      itemOptions: [],
      operationOptions: [],
      invoiceOptions: [],
      positionOptions: [],
      ordersLoading: false,
      ordersHasMore: false,
      orderDetailLoading: false,
      invoicesLoading: false,
      invoiceDetailLoading: false,
      // Сбой загрузки справочника отличаем от «данных нет»: иначе привязка молча не создаётся
      ordersError: false,
      orderDetailError: false,
      invoicesError: false,
      invoiceDetailError: false,
      errorMessage: '',
      retryAction: null,
      invoiceQuery: '',
      // Задания заказа: id задания → операции и позиция изделия в заказе
      orderTasks: [],
    };
  },
  computed: {
    summary() {
      return bindingSummary(this.modelValue || emptyBinding());
    },
  },
  watch: {
    modelValue: {
      handler(value) {
        // Родитель подменил строку целиком (загрузка документа, перенос состава из
        // расследования) — пересобираем внутреннее состояние из привязки
        if (this.isSameAsInternal(value)) return;
        this.syncFromModel(value);
      },
      deep: true,
    },
  },
  mounted() {
    this.syncFromModel(this.modelValue);
  },
  methods: {
    /**
     * Ошибку загрузки справочника не проглатываем: пишем в консоль, показываем тост
     * и оставляем сообщение с кнопкой «Повторить» — иначе сбой выглядит как «данных нет».
     */
    reportError(error, fallback, retry = null) {
      console.error(fallback, error);
      const message = error?.response?.data?.message || fallback;
      this.errorMessage = message;
      this.retryAction = retry;
      if (this.$root?.$toast?.error) this.$root.$toast.error(message);
    },
    clearError() {
      this.errorMessage = '';
      this.retryAction = null;
    },
    retryLastLoad() {
      const retry = this.retryAction;
      this.clearError();
      if (retry) retry();
    },
    /** Внутреннее состояние уже соответствует привязке — лишний пересбор не нужен. */
    isSameAsInternal(value) {
      const current = this.buildBinding();
      return (
        (value?.batch_product_id ?? null) === current.batch_product_id
        && (value?.production_order_id ?? null) === current.production_order_id
        && (value?.production_task_id ?? null) === current.production_task_id
        && (value?.production_task_operation_id ?? null) === current.production_task_operation_id
        && (value?.receipt_invoice_id ?? null) === current.receipt_invoice_id
        && (value?.batch_manufacture_id ?? null) === current.batch_manufacture_id
      );
    },
    /** Собрать селекторы из сохранённой привязки (объекты подгружены бэкендом). */
    syncFromModel(value) {
      const binding = value || emptyBinding();
      this.scope = bindingScope(binding);

      if (this.scope === SCOPE_PRODUCTION) {
        this.selectedOrder = binding.production_order_id
          ? {
            id: binding.production_order_id,
            label: productionOrderLabel(binding.production_order) || 'Производственный заказ',
            raw: binding.production_order || null,
          }
          : null;
        this.selectedItem = (binding.batch_product_id || binding.production_task_id)
          ? {
            key: `bp-${binding.batch_product_id || 'none'}-task-${binding.production_task_id || 'none'}`,
            batch_product_id: binding.batch_product_id ?? null,
            production_task_id: binding.production_task_id ?? null,
            serial_number: binding.serial_number ?? null,
            product_id: null,
            product_name: null,
            label: batchProductLabel(binding.serial_number, null),
          }
          : null;
        this.selectedOperation = binding.production_task_operation_id
          ? {
            id: binding.production_task_operation_id,
            label: taskOperationLabel(binding.production_task_operation) || 'Операция',
            raw: binding.production_task_operation || null,
          }
          : null;
        this.selectedInvoice = null;
        this.selectedPosition = null;
        this.itemOptions = this.selectedItem ? [this.selectedItem] : [];
        this.operationOptions = this.selectedOperation ? [this.selectedOperation] : [];
        // Полный состав заказа догружаем, чтобы можно было сменить изделие или операцию
        if (this.selectedOrder && !this.readOnly) this.fetchOrderDetail(this.selectedOrder.id);
      } else if (this.scope === SCOPE_RECEIPT) {
        this.selectedInvoice = binding.receipt_invoice_id
          ? {
            id: binding.receipt_invoice_id,
            label: receiptInvoiceLabel(binding.receipt_invoice) || 'Приходная накладная',
            raw: binding.receipt_invoice || null,
          }
          : null;
        this.selectedPosition = (binding.batch_product_id || binding.batch_manufacture_id)
          ? {
            key: `pos-${binding.batch_product_id || 'none'}-${binding.batch_manufacture_id || 'none'}`,
            batch_product_id: binding.batch_product_id ?? null,
            batch_manufacture_id: binding.batch_manufacture_id ?? null,
            batch_manufacture: binding.batch_manufacture || null,
            serial_number: binding.serial_number ?? null,
            product_id: null,
            product_name: null,
            label: batchManufactureLabel(binding.batch_manufacture) || 'Позиция накладной',
          }
          : null;
        this.selectedOrder = null;
        this.selectedItem = null;
        this.selectedOperation = null;
        this.positionOptions = this.selectedPosition ? [this.selectedPosition] : [];
        if (this.selectedInvoice && !this.readOnly) this.fetchInvoiceDetail(this.selectedInvoice.id);
      } else {
        this.selectedOrder = null;
        this.selectedItem = null;
        this.selectedOperation = null;
        this.selectedInvoice = null;
        this.selectedPosition = null;
      }
    },
    /** Собрать привязку из выбранного. */
    buildBinding() {
      const binding = emptyBinding();
      binding.nonconformity_case_id = this.modelValue?.nonconformity_case_id ?? null;

      if (this.scope === SCOPE_PRODUCTION) {
        binding.production_order_id = this.selectedOrder?.id ?? null;
        binding.production_order = this.selectedOrder?.raw ?? null;
        binding.batch_product_id = this.selectedItem?.batch_product_id ?? null;
        binding.production_task_id = this.selectedItem?.production_task_id ?? null;
        binding.serial_number = this.selectedItem?.serial_number ?? null;
        binding.production_task_operation_id = this.selectedOperation?.id ?? null;
        binding.production_task_operation = this.selectedOperation?.raw ?? null;
      } else if (this.scope === SCOPE_RECEIPT) {
        binding.receipt_invoice_id = this.selectedInvoice?.id ?? null;
        binding.receipt_invoice = this.selectedInvoice?.raw ?? null;
        binding.batch_product_id = this.selectedPosition?.batch_product_id ?? null;
        binding.batch_manufacture_id = this.selectedPosition?.batch_manufacture_id ?? null;
        binding.batch_manufacture = this.selectedPosition?.batch_manufacture ?? null;
        binding.serial_number = this.selectedPosition?.serial_number ?? null;
      }

      return binding;
    },
    emitBinding() {
      this.$emit('update:modelValue', this.buildBinding());
    },
    onScopeChange(value) {
      this.scope = value ?? null;
      this.selectedOrder = null;
      this.selectedItem = null;
      this.selectedOperation = null;
      this.selectedInvoice = null;
      this.selectedPosition = null;
      this.itemOptions = [];
      this.operationOptions = [];
      this.positionOptions = [];
      this.orderDetailError = false;
      this.invoiceDetailError = false;
      this.clearError();
      if (this.scope === SCOPE_PRODUCTION) this.ensureOrders();
      this.emitBinding();
    },
    // --- Производственный контур ---
    async ensureOrders() {
      // Список заказов общий для всех строк документа — сначала берём уже загруженный
      const cached = loadedProductionOrders();
      if (cached.length) {
        this.orderOptions = this.mapOrderOptions(cached);
        this.ordersHasMore = productionOrdersHasMore();
        return;
      }
      if (this.ordersLoading) return;
      await this.loadMoreOrders();
    },
    async loadMoreOrders() {
      this.ordersLoading = true;
      this.ordersError = false;
      try {
        const rows = await loadMoreProductionOrders();
        this.orderOptions = this.mapOrderOptions(rows);
        this.ordersHasMore = productionOrdersHasMore();
        this.clearError();
      } catch (e) {
        this.ordersHasMore = false;
        this.ordersError = true;
        this.reportError(e, 'Не удалось загрузить список производственных заказов', () => this.loadMoreOrders());
      } finally {
        this.ordersLoading = false;
      }
    },
    mapOrderOptions(rows) {
      return (rows || []).map((order) => ({
        id: order.id,
        label: productionOrderLabel(order),
        raw: order,
      }));
    },
    onOrderChange(option) {
      this.selectedOrder = option || null;
      this.selectedItem = null;
      this.selectedOperation = null;
      this.itemOptions = [];
      this.operationOptions = [];
      this.emitBinding();
      if (option?.id) this.fetchOrderDetail(option.id);
    },
    async fetchOrderDetail(orderId) {
      this.orderDetailLoading = true;
      this.orderDetailError = false;
      try {
        const order = await loadProductionOrder(orderId);
        this.orderTasks = order?.tasks || [];
        this.itemOptions = this.buildItemOptions(order);
        this.restoreSelectedItem();
        this.clearError();
      } catch (e) {
        this.orderTasks = [];
        this.itemOptions = [];
        this.orderDetailError = true;
        this.reportError(e, 'Не удалось загрузить изделия выбранного заказа', () => this.fetchOrderDetail(orderId));
      } finally {
        this.orderDetailLoading = false;
      }
    },
    buildItemOptions(order) {
      const options = [];
      (order?.tasks || []).forEach((task, taskIndex) => {
        const position = taskIndex + 1;
        const batchProducts = task.batch_products || [];
        if (batchProducts.length === 0) {
          options.push({
            key: `task-${task.id}`,
            batch_product_id: null,
            production_task_id: task.id,
            serial_number: null,
            product_id: order?.order_product?.product?.id || order?.product_id || null,
            product_name: order?.order_product?.product?.name || order?.product?.name || null,
            label: batchProductLabel(null, position),
          });
          return;
        }
        batchProducts.forEach((batchProduct) => {
          options.push({
            key: `bp-${batchProduct.id}`,
            batch_product_id: batchProduct.id,
            production_task_id: task.id,
            serial_number: batchProduct.serial_number || null,
            product_id: batchProduct.product_id || order?.order_product?.product?.id || order?.product_id || null,
            product_name: order?.order_product?.product?.name || order?.product?.name || null,
            label: batchProductLabel(batchProduct.serial_number, position),
          });
        });
      });
      return options;
    },
    /** После догрузки заказа подменяем «заглушку» выбранного изделия полной опцией. */
    restoreSelectedItem() {
      if (!this.selectedItem) return;
      const found = this.itemOptions.find((o) => (
        (this.selectedItem.batch_product_id && o.batch_product_id === this.selectedItem.batch_product_id)
        || (!this.selectedItem.batch_product_id && o.production_task_id === this.selectedItem.production_task_id)
      ));
      if (!found) return;
      this.selectedItem = found;
      this.operationOptions = this.buildOperationOptions(found.production_task_id);
      if (this.selectedOperation) {
        const operation = this.operationOptions.find((o) => o.id === this.selectedOperation.id);
        if (operation) this.selectedOperation = operation;
      }
      this.emitBinding();
    },
    buildOperationOptions(taskId) {
      const task = (this.orderTasks || []).find((t) => t.id === taskId);
      const operations = [...(task?.operations || [])];
      operations.sort((a, b) => (a.operation?.sort ?? 0) - (b.operation?.sort ?? 0));
      return operations.map((operation) => ({
        id: operation.id,
        label: taskOperationLabel(operation),
        raw: operation,
      }));
    },
    onItemChange(option) {
      this.selectedItem = option || null;
      this.selectedOperation = null;
      this.operationOptions = option ? this.buildOperationOptions(option.production_task_id) : [];
      this.emitBinding();
      if (option) {
        this.$emit('product-picked', {
          product_id: option.product_id || null,
          product_name: option.product_name || null,
          serial_number: option.serial_number || null,
        });
      }
    },
    onOperationChange(option) {
      this.selectedOperation = option || null;
      this.emitBinding();
    },
    // --- Приёмочный контур ---
    onInvoiceSearch(query) {
      this.invoiceQuery = query || '';
      clearTimeout(this._invoiceTimer);
      this._invoiceTimer = setTimeout(() => this.fetchInvoices(), 300);
    },
    async fetchInvoices() {
      this.invoicesLoading = true;
      this.invoicesError = false;
      try {
        const { rows } = await searchReceiptInvoices(this.invoiceQuery.trim(), 1, 20);
        this.invoiceOptions = rows.map((invoice) => ({
          id: invoice.id,
          label: receiptInvoiceLabel(invoice),
          raw: invoice,
        }));
        this.clearError();
      } catch (e) {
        this.invoiceOptions = [];
        this.invoicesError = true;
        this.reportError(e, 'Не удалось загрузить приходные накладные', () => this.fetchInvoices());
      } finally {
        this.invoicesLoading = false;
      }
    },
    onInvoiceChange(option) {
      this.selectedInvoice = option || null;
      this.selectedPosition = null;
      this.positionOptions = [];
      this.emitBinding();
      if (option?.id) this.fetchInvoiceDetail(option.id);
    },
    async fetchInvoiceDetail(invoiceId) {
      this.invoiceDetailLoading = true;
      this.invoiceDetailError = false;
      try {
        const invoice = await loadReceiptInvoice(invoiceId);
        this.positionOptions = this.buildPositionOptions(invoice);
        this.restoreSelectedPosition();
        this.clearError();
      } catch (e) {
        this.positionOptions = [];
        this.invoiceDetailError = true;
        this.reportError(e, 'Не удалось загрузить позиции накладной', () => this.fetchInvoiceDetail(invoiceId));
      } finally {
        this.invoiceDetailLoading = false;
      }
    },
    buildPositionOptions(invoice) {
      const options = [];
      (invoice?.products || []).forEach((position) => {
        const productName = position.product?.name || 'Позиция без номенклатуры';
        const batchProducts = position.batch_products || [];
        if (batchProducts.length === 0) {
          options.push({
            key: `pos-${position.id}`,
            batch_product_id: null,
            batch_manufacture_id: null,
            batch_manufacture: null,
            serial_number: null,
            product_id: position.product_id || null,
            product_name: productName,
            label: `${productName} · партия не указана`,
          });
          return;
        }
        batchProducts.forEach((batchProduct) => {
          const batch = batchProduct.batch_manufacture || null;
          const parts = [productName];
          const batchLabel = batchManufactureLabel(batch);
          if (batchLabel) parts.push(batchLabel);
          if (batchProduct.serial_number) parts.push(`серийный № ${batchProduct.serial_number}`);
          options.push({
            key: `pos-bp-${batchProduct.id}`,
            batch_product_id: batchProduct.id,
            batch_manufacture_id: batchProduct.batch_manufacture_id || batch?.id || null,
            batch_manufacture: batch,
            serial_number: batchProduct.serial_number || null,
            product_id: position.product_id || batchProduct.product_id || null,
            product_name: productName,
            label: parts.join(' · '),
          });
        });
      });
      return options;
    },
    /** После догрузки накладной подменяем «заглушку» выбранной позиции полной опцией. */
    restoreSelectedPosition() {
      if (!this.selectedPosition) return;
      const found = this.positionOptions.find((o) => (
        (this.selectedPosition.batch_product_id && o.batch_product_id === this.selectedPosition.batch_product_id)
        || (!this.selectedPosition.batch_product_id
          && this.selectedPosition.batch_manufacture_id
          && o.batch_manufacture_id === this.selectedPosition.batch_manufacture_id)
      ));
      if (!found) return;
      this.selectedPosition = found;
      this.emitBinding();
    },
    onPositionChange(option) {
      this.selectedPosition = option || null;
      this.emitBinding();
      if (option) {
        this.$emit('product-picked', {
          product_id: option.product_id || null,
          product_name: option.product_name || null,
          serial_number: option.serial_number || null,
        });
      }
    },
  },
};
</script>
